import React from 'react';
import Checkbox from 'src/components/Checkbox';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import { ITableColumn } from 'src/components/Table/types';
import { UnitBulkOperationDto } from 'src/SharedCustomHooks/UsePropertySuites/types';
import { getDisabledCellStyle } from 'src/utils/CommonHelpers/commonhelpers';
import { variables } from 'src/Tenants';

interface CreateSuiteTableColumnsParams {
  allSelected: boolean;
  onSelectAll: () => void;
  selectedSuiteIds: string[];
  onSuiteSelect: (suiteId: string) => void;
  sendSchedulingEmail: boolean
}

const getHomeOwnerName = (homeOwner: {
  firstname: string | null;
  lastname: string | null;
} | null) => {
  if (!homeOwner || (!homeOwner.firstname && !homeOwner.lastname)) {
    return 'No Owner';
  }
  return `${homeOwner.firstname || ''} ${homeOwner.lastname || ''}`.trim();
};

export const createSuiteTableColumns = ({
  allSelected,
  onSelectAll,
  selectedSuiteIds,
  onSuiteSelect,
  sendSchedulingEmail
}: CreateSuiteTableColumnsParams): ITableColumn[] => {
  const suiteName: string = variables.Suite;

  return [
    {
      label: <Checkbox readOnly value={allSelected} onChange={onSelectAll} />,
      style: { width: '18rem' },
      cell: (data: UnitBulkOperationDto) => {
        const isDisabled: boolean = sendSchedulingEmail && !data.hasPrimaryContact;
        const isSelected: boolean = selectedSuiteIds.includes(data.id);

        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            ...getDisabledCellStyle(isDisabled),
          }}>
            <Checkbox
              checked={isSelected}
              name={data.id}
              value={isSelected}
              onChange={() => onSuiteSelect(data.id)}
              disabled={isDisabled}
              readOnly
            />
            {isDisabled && (
              <CategoryPill
                text="No Primary Contact"
                color="#dc3545"
                textColor="white"
                size="small"
              />
            )}
          </div>
        );
      },
    },
    {
      label: `${suiteName}`,
      accessor: 'number',
      sortable: true,
      style: { width: '8rem' },
      cell: (data: UnitBulkOperationDto) => {
        const isDisabled: boolean = sendSchedulingEmail && !data.hasPrimaryContact;

        return (
          <div style={getDisabledCellStyle(isDisabled)}>
            {data.number}
          </div>
        );
      },
    },
    {
      label: 'Status',
      accessor: 'status',
      sortable: true,
      style: { width: '5rem' },
      cell: (data: UnitBulkOperationDto) => {
        const isDisabled: boolean = sendSchedulingEmail && !data.hasPrimaryContact;

        return (
          <div style={getDisabledCellStyle(isDisabled)}>
            {data.status}
          </div>
        );
      },
    },
    {
      label: 'Areas',
      sortable: true,
      style: { width: '10rem' },
      cell: (data: UnitBulkOperationDto) => {
        const isDisabled: boolean = sendSchedulingEmail && !data.hasPrimaryContact;

        return (
          <div style={getDisabledCellStyle(isDisabled)}>
            {`${data.areaCount} room${data.areaCount !== 1 ? 's' : ''}`}
          </div>
        );
      },
      sortingAlgorithm: (a: UnitBulkOperationDto, b: UnitBulkOperationDto) =>
        a.areaCount - b.areaCount,
    },
    {
      label: 'Home Owner',
      sortable: true,
      style: { width: '12rem' },
      cell: (data: UnitBulkOperationDto) => {
        const isDisabled: boolean = sendSchedulingEmail && !data.hasPrimaryContact;

        return (
          <div style={getDisabledCellStyle(isDisabled)}>
            {getHomeOwnerName(data.homeOwner)}
          </div>
        );
      },
      sortingAlgorithm: (a: UnitBulkOperationDto, b: UnitBulkOperationDto) => {
        const nameA: string = getHomeOwnerName(a.homeOwner);
        const nameB: string = getHomeOwnerName(b.homeOwner);
        return nameA.localeCompare(nameB);
      },
    },
  ];
};